import { z } from 'zod';
import type { StyleState } from '@/components/map/utils/layer-style-helpers';
import { ColumnMetadata, LayerStats, StyleConfig } from '@/components/maplibre/types';
import { APP_SETTINGS } from '@/lib/config';
import { LAYER_GEOMETRY_TYPE } from '@/lib/db-constants';
import type { LayerGeometryType } from '@/lib/db-row-types';
import { getBorderDashArray, getBorderLineGapWidth } from '@/utils/layer-sources/style-borders';
import {
  DEFAULT_SCHEMES,
  DEFAULT_STYLE_VALUES,
  LABEL_FONT_STYLES,
  LABEL_FONT_STYLE_VALUES,
  LABEL_MODE,
  STYLE_MODE,
  STYLE_PROPERTY_DIRECTION,
} from './style-constants';

/**
 * Style V2 - Property-scoped styling per layer
 * Implements the Style Specification V2
 *
 * Quick summary (2.3.0):
 * - Icon support for point layers: added icon property to PointStyleSchema with fixed/category modes.
 *   Supports rendering points as icons instead of circles. icon.value: null renders as circles (backward compatible).
 *
 * Version History:
 * - 2.3.0: Icon support for point layers (icon property with fixed mode)
 * - 2.2.0: Point label placement for points (textVariableAnchor, textRadialOffset) + defaults
 * - 2.1.0: Added data range support (dataMin/dataMax) for polygon fill, line, and point color range styling
 * - 2.0.0: Initial V2 release with property-scoped styling
 */

export type StyleModeValue = (typeof STYLE_MODE)[keyof typeof STYLE_MODE];

export const SemverSchema = z.string().regex(/^\d+\.\d+\.\d+$/, 'Invalid semver format');
export const ColumnIdSchema = z.union([z.number(), z.string()]).nullable();
export const ColumnNameSchema = z.string();
export const BorderTypeSchema = z.enum(['solid', 'dashed', 'dotted', 'double']);

export type Semver = z.infer<typeof SemverSchema>;
export type ColumnId = z.infer<typeof ColumnIdSchema>;
export type BorderType = z.infer<typeof BorderTypeSchema>;

// Static mode for fixed values
export const StaticNumberSchema = z.object({
  mode: z.literal(STYLE_MODE.FIXED),
  value: z.number(),
});

export const StaticStringSchema = z.object({
  mode: z.literal(STYLE_MODE.FIXED),
  value: z.string(),
});

export const StaticSchema = z.union([StaticNumberSchema, StaticStringSchema]);
export type Static = z.infer<typeof StaticSchema>;

// Zoom-driven label sizing: text/halo size interpolates linearly with map zoom
// between (minZoom, minSize) and (maxZoom, maxSize). Mirrors OpacityModeSchema's
// fixed-vs-variant union pattern.
export const LabelSizeZoomRangeSchema = z
  .object({
    mode: z.literal(STYLE_MODE.ZOOM_RANGE),
    minZoom: z.number().min(0).max(24),
    maxZoom: z.number().min(0).max(24),
    minSize: z.number().nonnegative(),
    maxSize: z.number().nonnegative(),
  })
  .refine(v => v.minZoom < v.maxZoom, {
    message: 'minZoom must be less than maxZoom',
    path: ['minZoom'],
  });

export type LabelSizeZoomRange = z.infer<typeof LabelSizeZoomRangeSchema>;

// Label size/halo width accept either a fixed value or a zoom-driven range.
// Plain z.union (not z.discriminatedUnion) because the zoom-range member carries
// a .refine() — that makes it a ZodEffects, which z.discriminatedUnion rejects.
export const LabelSizeModeSchema = z.union([StaticNumberSchema, LabelSizeZoomRangeSchema]);
export type LabelSizeMode = z.infer<typeof LabelSizeModeSchema>;

export const LabelFontStyleSchema = z
  .enum(LABEL_FONT_STYLE_VALUES)
  .default(LABEL_FONT_STYLES.REGULAR);

// LabelText: unified label schema (mode + parts)
export const LabelTextModeSchema = z.enum([
  LABEL_MODE.NONE,
  LABEL_MODE.SINGLE_COLUMN,
  LABEL_MODE.TEMPLATE,
]);
export type LabelTextMode = z.infer<typeof LabelTextModeSchema>;

const LabelTextPartLiteralSchema = z.object({
  type: z.literal('literal'),
  text: z.string(),
});

const LabelTextPartColumnSchema = z.object({
  type: z.literal('column'),
  columnId: z.union([z.number(), z.string()]).nullable(),
  columnName: z.string().optional(),
  format: z
    .object({
      kind: z.enum(['string', 'number', 'date']).optional(),
      number: z
        .object({
          decimals: z.number().int().min(0).max(6).optional(),
          compact: z.boolean().optional(),
        })
        .optional(),
      date: z
        .object({
          pattern: z.string().optional(),
          timezone: z.string().optional(),
        })
        .optional(),
      fallback: z.string().optional(),
    })
    .optional(),
});

export const LabelTextPartSchema = z.union([LabelTextPartLiteralSchema, LabelTextPartColumnSchema]);
export type LabelTextPart = z.infer<typeof LabelTextPartSchema>;

export const LabelTextSchema = z
  .object({
    mode: LabelTextModeSchema,
    parts: z.array(LabelTextPartSchema).optional(),
  })
  .superRefine((val, ctx) => {
    const parts = val.parts ?? [];
    if (val.mode === LABEL_MODE.NONE) {
      if (parts.length > 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'NONE mode must not include parts' });
      }
      return;
    }
    if (val.mode === LABEL_MODE.SINGLE_COLUMN) {
      if (parts.length !== 1 || parts[0].type !== 'column') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'SINGLE_COLUMN mode requires exactly one column part',
        });
      }
      return;
    }
    if (val.mode === LABEL_MODE.TEMPLATE) {
      if (parts.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'TEMPLATE mode requires at least one part',
        });
      }
    }
  });
export type LabelText = z.infer<typeof LabelTextSchema>;

export const LabelStyleSchema = z.object({
  columnId: ColumnIdSchema.nullable(),
  columnName: ColumnNameSchema.optional(),
  // Unified label text representation (preferred)
  labelText: LabelTextSchema.optional(),
  size: LabelSizeModeSchema,
  color: StaticStringSchema.nullable(),
  haloColor: StaticStringSchema,
  haloWidth: LabelSizeModeSchema,
  fontStyle: LabelFontStyleSchema,
  textVariableAnchor: z
    .enum([
      'center',
      'left',
      'right',
      'top',
      'bottom',
      'top-left',
      'top-right',
      'bottom-left',
      'bottom-right',
    ])
    .optional(),
  textRadialOffset: z.number().optional(),
});

export type LabelStyle = z.infer<typeof LabelStyleSchema>;

// Range mode schema
export const DirectionSchema = z
  .enum([STYLE_PROPERTY_DIRECTION.ASCENDING, STYLE_PROPERTY_DIRECTION.DESCENDING])
  .default(STYLE_PROPERTY_DIRECTION.ASCENDING);
export type Direction = z.infer<typeof DirectionSchema>;

export const MinMaxRangeSchema = z.object({
  mode: z.literal(STYLE_MODE.RANGE),
  columnId: ColumnIdSchema,
  columnName: ColumnNameSchema.optional(),
  min: z.number(),
  max: z.number(),
  direction: DirectionSchema.optional(),
});

export type MinMaxRange = z.infer<typeof MinMaxRangeSchema>;

export const CustomColorsSchema = z.object({
  category: z.string(),
  color: z.string(),
});

export type CustomColorsConfig = z.infer<typeof CustomColorsSchema>;

// Color modes (category/range use named palettes or scales)
const ColorCategorySchema = z.object({
  mode: z.literal(STYLE_MODE.CATEGORY),
  columnId: ColumnIdSchema,
  columnName: ColumnNameSchema.optional(),
  scheme: z.string().nullable(),
  customColors: z.array(CustomColorsSchema).nullable().optional(),
});

const ColorRangeSchema = z.object({
  mode: z.literal(STYLE_MODE.RANGE),
  columnId: ColumnIdSchema,
  columnName: ColumnNameSchema.optional(),
  scheme: z.string(),
  dataMin: z
    .number()
    .nullable()
    .optional()
    .transform(v => v ?? undefined),
  dataMax: z
    .number()
    .nullable()
    .optional()
    .transform(v => v ?? undefined),
});

const ColorModeSchema = z.union([StaticStringSchema, ColorCategorySchema, ColorRangeSchema]);

export type ColorMode = z.infer<typeof ColorModeSchema>;
export type ColorModeType = ColorMode['mode'];

// Numeric modes schema (e.g., width, size)
const NumericModeSchema = z.union([StaticNumberSchema, MinMaxRangeSchema]);
export type NumericMode = z.infer<typeof NumericModeSchema>;

// Opacity mode schema (0..1)
const OpacityFixedSchema = z.object({
  mode: z.literal(STYLE_MODE.FIXED),
  value: z.number().min(0).max(1),
});

const OpacityRangeSchema = z.object({
  mode: z.literal(STYLE_MODE.RANGE),
  columnId: ColumnIdSchema,
  columnName: ColumnNameSchema.optional(),
  min: z.number().min(0).max(1),
  max: z.number().min(0).max(1),
  direction: DirectionSchema.optional(),
});

const OpacityModeSchema = z.union([OpacityFixedSchema, OpacityRangeSchema]);

export type OpacityMode = z.infer<typeof OpacityModeSchema>;

const IconCustomMappingSchema = z.object({
  category: z.string(),
  value: z.string(),
});

const IconFixedSchema = z.object({
  mode: z.literal(STYLE_MODE.FIXED),
  value: z.string().nullable(),
});

const IconCategorySchema = z.object({
  mode: z.literal(STYLE_MODE.CATEGORY),
  columnId: ColumnIdSchema,
  columnName: ColumnNameSchema.optional(),
  customIcons: z.array(IconCustomMappingSchema).nullable().optional(),
});

const IconModeSchema = z.union([IconFixedSchema, IconCategorySchema]);

export type IconCustomMapping = z.infer<typeof IconCustomMappingSchema>;
export type IconFixed = z.infer<typeof IconFixedSchema>;
export type IconCategory = z.infer<typeof IconCategorySchema>;
export type IconMode = z.infer<typeof IconModeSchema>;
export type IconModeType = IconMode['mode'];

// Polygon schemas - separated into fill and border
const PolygonFillSchema = z.object({
  color: ColorModeSchema,
  opacity: OpacityModeSchema,
});

const PolygonBorderSchema = z.object({
  color: ColorModeSchema,
  opacity: OpacityModeSchema,
  width: NumericModeSchema,
  borderType: BorderTypeSchema,
});

const PolygonStyleSchema = z.object({
  fill: PolygonFillSchema,
  border: PolygonBorderSchema,
  label: LabelStyleSchema.optional(),
});

export type PolygonFill = z.infer<typeof PolygonFillSchema>;
export type PolygonBorder = z.infer<typeof PolygonBorderSchema>;
export type PolygonStyle = z.infer<typeof PolygonStyleSchema>;

// Line schema
const LineStyleSchema = z.object({
  color: ColorModeSchema,
  opacity: OpacityModeSchema,
  width: NumericModeSchema,
  borderType: BorderTypeSchema,
});
export type LineStyle = z.infer<typeof LineStyleSchema>;

// Point schema
const PointStyleSchema = z.object({
  icon: IconModeSchema,
  size: NumericModeSchema,
  color: ColorModeSchema,
  opacity: OpacityModeSchema,
  strokeColor: ColorModeSchema,
  strokeOpacity: OpacityModeSchema,
  strokeWidth: NumericModeSchema,
  label: LabelStyleSchema.optional(),
});

export type PointStyle = z.infer<typeof PointStyleSchema>;

// Raster schema — raster layers have no feature geometry, just a render opacity.
const RasterStyleSchema = z.object({
  opacity: z.number().min(0).max(1),
});
export type RasterStyle = z.infer<typeof RasterStyleSchema>;

// Root schema
export const StyleV2Schema = z.object({
  version: SemverSchema,
  polygon: PolygonStyleSchema.nullable()
    .optional()
    .transform(v => v ?? undefined),
  line: LineStyleSchema.nullable()
    .optional()
    .transform(v => v ?? undefined),
  point: PointStyleSchema.nullable()
    .optional()
    .transform(v => v ?? undefined),
  raster: RasterStyleSchema.nullable()
    .optional()
    .transform(v => v ?? undefined),
});

export type StyleV2 = z.infer<typeof StyleV2Schema>;

// ============================================================================
// Validation & Type Guards
// ============================================================================

export function validateStyleV2(data: unknown): StyleV2 {
  return StyleV2Schema.parse(data);
}

export function safeValidateStyleV2(data: unknown): {
  success: boolean;
  data?: StyleV2;
  error?: z.ZodError;
} {
  const result = StyleV2Schema.safeParse(data);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.error };
}

export function isStyleV2(data: unknown): data is StyleV2 {
  if (typeof data !== 'object' || data === null) return false;
  const obj = data as Record<string, unknown>;
  return typeof obj.version === 'string' && obj.version.startsWith('2.');
}

export function isStyleV1(data: unknown): boolean {
  if (typeof data !== 'object' || data === null) return false;
  const obj = data as Record<string, unknown>;
  return !obj.version;
}

// ============================================================================
// Column ID/Name Resolution Helpers
// ============================================================================

/**
 * Resolves a column name to its column ID
 * Used when saving styles - converts UI column names to persistent IDs
 */
export function resolveColumnId(
  columnNameOrId: string | null,
  columnMetadata?: ColumnMetadata[]
): string | number | null {
  if (!columnNameOrId) return null;
  if (!columnMetadata || columnMetadata.length === 0) return columnNameOrId;

  // Find the column by name
  const column = columnMetadata.find(col => col.column_name === columnNameOrId);
  return column ? column.column_id : columnNameOrId;
}

/**
 * Resolves a column ID back to its column name
 * Used when loading styles - converts persistent IDs to UI column names for MapLibre expressions
 */
export function resolveColumnName(
  columnIdOrName: string | number | null,
  columnMetadata?: ColumnMetadata[],
  stats?: LayerStats
): string | null {
  if (columnIdOrName === null || columnIdOrName === undefined) return null;

  if (columnMetadata) {
    const column = columnMetadata.find(col => col.column_id == columnIdOrName);
    if (column) return column.column_name;
  }

  if (stats) {
    const property = stats.fields.find(field => field.id == (columnIdOrName as unknown as string));
    if (property) return property.name ?? null;
  }

  return null;
}

// ============================================================================
// Helper Functions
// ============================================================================

export function createStaticMode<T>(value: T) {
  return { mode: STYLE_MODE.FIXED, value };
}

/**
 * Normalizes a label size/halo value into a LabelSizeMode.
 * Returns variant objects (fixed or zoom-range) unchanged so a stored
 * zoom-range survives serialization; wraps a bare number as fixed mode.
 */
export function toLabelSizeMode(value: number | LabelSizeMode): LabelSizeMode {
  if (typeof value === 'number') {
    return { mode: STYLE_MODE.FIXED, value };
  }
  return value;
}

/**
 * Collapses a LabelSizeMode to a single number for consumers that cannot
 * express a zoom-driven value (UI inputs, the v1 StyleConfig fallback).
 * For zoom-range, returns maxSize as the representative scalar.
 */
export function labelSizeModeToNumber(mode: LabelSizeMode | undefined, fallback: number): number {
  if (!mode) return fallback;
  if (mode.mode === STYLE_MODE.ZOOM_RANGE) return mode.maxSize;
  return mode.value;
}

export function createRangeMode(
  columnId: ColumnId,
  min: number,
  max: number,
  direction?: Direction
) {
  return MinMaxRangeSchema.parse({ mode: STYLE_MODE.RANGE, columnId, min, max, direction });
}

export function createOpacityRangeMode(
  columnId: ColumnId,
  min: number,
  max: number,
  direction?: Direction
): OpacityMode {
  return OpacityRangeSchema.parse({ mode: STYLE_MODE.RANGE, columnId, min, max, direction });
}

export function createFixedColorMode(value: string) {
  return { mode: STYLE_MODE.FIXED, value } as const;
}

export function createCategoryIconMode(
  columnId: ColumnId,
  customIcons: IconCustomMapping[] | null = null
) {
  return IconCategorySchema.parse({
    mode: STYLE_MODE.CATEGORY,
    columnId,
    customIcons,
  });
}

export function createCategoryColorMode(
  columnId: ColumnId,
  scheme: string | null = DEFAULT_SCHEMES.CATEGORY,
  customColors: CustomColorsConfig[] | null = null
) {
  return ColorCategorySchema.parse({
    mode: STYLE_MODE.CATEGORY,
    columnId,
    scheme,
    customColors,
  });
}

export function createRangeColorMode(
  columnId: ColumnId,
  scheme: string | null = DEFAULT_SCHEMES.RANGE,
  dataMin?: number,
  dataMax?: number
) {
  return ColorRangeSchema.parse({
    mode: STYLE_MODE.RANGE,
    columnId,
    scheme,
    dataMin,
    dataMax,
  });
}

export function resolveLabelTextColumns(
  labelText: LabelText | undefined,
  columnMetadata?: ColumnMetadata[],
  stats?: LayerStats
): LabelText | undefined {
  if (!labelText) return undefined;
  if (labelText.mode === LABEL_MODE.NONE) return labelText;

  if (labelText.parts) {
    return {
      ...labelText,
      parts: labelText.parts.map(part => {
        if (part.type === 'column') {
          const name = resolveColumnName(part.columnId, columnMetadata, stats);
          return { ...part, columnName: name || undefined };
        }
        return part;
      }),
    };
  }

  return labelText;
}

/**
 * Converts StyleState (UI format) to StyleV2 (persistence format)
 * Uses column names directly for dynamic styling
 */
export function convertStyleStateToV2(
  styleState: StyleState,
  geometryTypes: Set<LayerGeometryType>,
  columnMetadata?: ColumnMetadata[]
): StyleV2 {
  const v2Style: StyleV2 = {
    version: APP_SETTINGS.CURRENT_STYLE_VERSION,
  };

  const toDecimal = (percentage: number): number => percentage / 100;

  const createColorMode = (staticColor: string, useColorLogic: boolean): ColorMode => {
    if (useColorLogic && styleState.colorLogicEnabled && styleState.colorLogicColumn) {
      const columnName = styleState.colorLogicColumn;
      const columnId = resolveColumnId(columnName, columnMetadata) as number | null;

      if (styleState.colorLogicType === 'category') {
        return {
          mode: STYLE_MODE.CATEGORY,
          columnId,
          scheme: styleState.colorLogicScheme,
          customColors: styleState.customColors,
        };
      } else {
        return createRangeColorMode(columnId, styleState.colorLogicScheme);
      }
    }
    return createStaticMode(staticColor);
  };

  if (geometryTypes.has(LAYER_GEOMETRY_TYPE.POLYGON)) {
    const fillColor = createColorMode(styleState.polygonFillColor, true);

    v2Style.polygon = {
      fill: {
        color: fillColor,
        opacity: createStaticMode(toDecimal(styleState.polygonFillOpacity)),
      },
      border: {
        color: createStaticMode(styleState.polygonStrokeColor),
        opacity: createStaticMode(toDecimal(styleState.polygonStrokeOpacity)),
        width: createStaticMode(styleState.polygonStrokeWidth),
        borderType: styleState.borderType as BorderType,
      },
      label: {
        columnId: resolveColumnId(styleState.labelLogic.labelBy, columnMetadata) as number | null,
        labelText: styleState.labelLogic.labelText,
        size: toLabelSizeMode(styleState.labelLogic.size),
        color: createStaticMode(styleState.labelLogic.color),
        haloColor: createStaticMode(styleState.labelLogic.haloColor),
        haloWidth: toLabelSizeMode(styleState.labelLogic.haloWidth),
        fontStyle: styleState.labelLogic.fontStyle,
      },
    };
  }

  if (geometryTypes.has(LAYER_GEOMETRY_TYPE.LINE_STRING)) {
    const lineColor = createColorMode(
      styleState.lineColor,
      !geometryTypes.has(LAYER_GEOMETRY_TYPE.POLYGON)
    );

    v2Style.line = {
      color: lineColor,
      opacity: createStaticMode(toDecimal(styleState.lineOpacity)),
      width: createStaticMode(styleState.lineWidth),
      borderType: styleState.borderType as BorderType,
    };
  }

  if (geometryTypes.has(LAYER_GEOMETRY_TYPE.POINT)) {
    let sizeMode: NumericMode = createStaticMode(styleState.size);
    if (styleState.sizeLogic.sizeBy) {
      const sizeColumnName = styleState.sizeLogic.sizeBy;
      sizeMode = createRangeMode(
        resolveColumnId(sizeColumnName, columnMetadata) as number | null,
        styleState.sizeLogic.minSize,
        styleState.sizeLogic.maxSize
      );
    }

    const pointColor = createColorMode(
      styleState.fillColor,
      !geometryTypes.has(LAYER_GEOMETRY_TYPE.POLYGON)
    );

    v2Style.point = {
      icon: { mode: STYLE_MODE.FIXED, value: null },
      size: sizeMode,
      color: pointColor,
      opacity: createStaticMode(toDecimal(styleState.fillOpacity)),
      strokeColor: createStaticMode(styleState.strokeColor),
      strokeOpacity: createStaticMode(toDecimal(styleState.strokeOpacity)),
      strokeWidth: createStaticMode(styleState.strokeWidth),
      label: {
        columnId: resolveColumnId(styleState.labelLogic.labelBy, columnMetadata) as number | null,
        labelText: styleState.labelLogic.labelText,
        size: toLabelSizeMode(styleState.labelLogic.size),
        color: createStaticMode(styleState.labelLogic.color),
        haloColor: createStaticMode(styleState.labelLogic.haloColor),
        haloWidth: toLabelSizeMode(styleState.labelLogic.haloWidth),
        fontStyle: styleState.labelLogic.fontStyle,
      },
    };
  }

  return v2Style;
}

/**
 * Converts StyleV2 to StyleConfig for MapLibre rendering
 * Extracts static values and preserves colorLogic/sizeLogic for dynamic styling
 */
export function styleV2ToConfig(params: {
  v2Style: StyleV2;
  columnMetadata?: ColumnMetadata[];
  stats?: LayerStats;
  geometryTypes: Set<LayerGeometryType>;
}): StyleConfig {
  const { v2Style, columnMetadata, stats, geometryTypes } = params;
  const config: StyleConfig = {};

  const hasPolygons = geometryTypes?.has(LAYER_GEOMETRY_TYPE.POLYGON) ?? false;
  const hasLines = geometryTypes?.has(LAYER_GEOMETRY_TYPE.LINE_STRING) ?? false;
  const hasPoints = geometryTypes?.has(LAYER_GEOMETRY_TYPE.POINT) ?? false;

  if (v2Style.point) {
    const size = v2Style.point.size;
    const color = v2Style.point.color;
    const opacity = v2Style.point.opacity;
    const strokeColor = v2Style.point.strokeColor;
    const strokeOpacity = v2Style.point.strokeOpacity;
    const strokeWidth = v2Style.point.strokeWidth;
    const icon = v2Style.point.icon;
    const label = v2Style.point.label;

    config.circle = {
      'circle-radius': size?.mode === STYLE_MODE.FIXED ? size.value : 6,
      'circle-color': color?.mode === STYLE_MODE.FIXED ? color.value : '#2DD4BF',
      'circle-opacity': opacity?.mode === STYLE_MODE.FIXED ? opacity.value : 1,
      'circle-stroke-color': strokeColor?.mode === STYLE_MODE.FIXED ? strokeColor.value : '#000000',
      'circle-stroke-width': strokeWidth?.mode === STYLE_MODE.FIXED ? strokeWidth.value : 1,
      'circle-stroke-opacity': strokeOpacity?.mode === STYLE_MODE.FIXED ? strokeOpacity.value : 1,
    };

    if (icon) {
      if (icon.mode === STYLE_MODE.FIXED && icon.value) {
        config.icon = {
          mode: STYLE_MODE.FIXED,
          value: icon.value,
        };
      } else if (icon.mode === STYLE_MODE.CATEGORY && icon.columnId) {
        config.icon = {
          mode: STYLE_MODE.CATEGORY,
          columnId: icon.columnId,
          columnName:
            resolveColumnName(icon.columnId, columnMetadata, stats) ?? icon.columnName ?? '',
          customIcons: icon.customIcons,
        };
      }
    }

    if (color && color.mode !== STYLE_MODE.FIXED) {
      config.colorLogic = {
        colorLogicColumn:
          resolveColumnName(color.columnId, columnMetadata, stats) ?? color.columnName ?? '',
        colorLogicScheme: color.scheme,
        colorLogicType: color.mode === STYLE_MODE.CATEGORY ? STYLE_MODE.CATEGORY : 'value',
        colorLogicEnabled: true,
        dataMin: color.mode === STYLE_MODE.RANGE ? color.dataMin : undefined,
        dataMax: color.mode === STYLE_MODE.RANGE ? color.dataMax : undefined,
        customColors: color.mode === STYLE_MODE.CATEGORY ? color.customColors : undefined,
      };
    }

    if (size && size.mode === STYLE_MODE.RANGE) {
      config.sizeLogic = {
        sizeBy: resolveColumnName(size.columnId, columnMetadata, stats) ?? size.columnName ?? '',
        minSize: size.min,
        maxSize: size.max,
        direction: size.direction,
      };
    }

    if (label && (label.columnId || label.labelText) && hasPoints) {
      config.labelLogic = {
        labelBy: resolveColumnName(label.columnId, columnMetadata, stats) ?? label.columnName ?? '',
        labelText: resolveLabelTextColumns(label.labelText, columnMetadata, stats),
        size: label.size
          ? toLabelSizeMode(label.size)
          : toLabelSizeMode(DEFAULT_STYLE_VALUES.labelLogic.size),
        color:
          label.color?.mode === STYLE_MODE.FIXED
            ? label.color.value
            : DEFAULT_STYLE_VALUES.labelLogic.color,
        haloColor:
          label.haloColor?.mode === STYLE_MODE.FIXED
            ? label.haloColor.value
            : DEFAULT_STYLE_VALUES.labelLogic.haloColor,
        haloWidth: label.haloWidth
          ? toLabelSizeMode(label.haloWidth)
          : toLabelSizeMode(DEFAULT_STYLE_VALUES.labelLogic.haloWidth),
        fontStyle: label.fontStyle ? label.fontStyle : DEFAULT_STYLE_VALUES.labelLogic.fontStyle,
        textVariableAnchor:
          label.textVariableAnchor ?? DEFAULT_STYLE_VALUES.labelLogic.textVariableAnchor,
        textRadialOffset:
          label.textRadialOffset ?? DEFAULT_STYLE_VALUES.labelLogic.textRadialOffset,
      };
    }

    if (opacity && opacity.mode === STYLE_MODE.RANGE) {
      config.opacityLogic = {
        propertyName: 'circle-opacity',
        column:
          resolveColumnName(opacity.columnId, columnMetadata, stats) ?? opacity.columnName ?? '',
        min: opacity.min,
        max: opacity.max,
        direction: opacity.direction,
      };
    }

    if (strokeOpacity && strokeOpacity.mode === STYLE_MODE.RANGE && !config.opacityLogic) {
      config.opacityLogic = {
        propertyName: 'circle-stroke-opacity',
        column:
          resolveColumnName(strokeOpacity.columnId, columnMetadata, stats) ??
          strokeOpacity.columnName ??
          '',
        min: strokeOpacity.min,
        max: strokeOpacity.max,
        direction: strokeOpacity.direction,
      };
    }

    if (strokeWidth && strokeWidth.mode === STYLE_MODE.RANGE) {
      config.widthLogic = {
        propertyName: 'circle-stroke-width',
        column:
          resolveColumnName(strokeWidth.columnId, columnMetadata, stats) ??
          strokeWidth.columnName ??
          '',
        min: strokeWidth.min,
        max: strokeWidth.max,
        direction: strokeWidth.direction,
      };
    }
  }

  if (v2Style.polygon) {
    const fill = v2Style.polygon.fill;
    const border = v2Style.polygon.border;
    const label = v2Style.polygon.label;

    if (fill) {
      config.fill = {
        'fill-color': fill.color?.mode === STYLE_MODE.FIXED ? fill.color.value : '#2DD4BF',
        'fill-opacity': fill.opacity?.mode === STYLE_MODE.FIXED ? fill.opacity.value : 0.8,
      };

      if (fill.color && fill.color.mode !== STYLE_MODE.FIXED) {
        config.colorLogic = {
          colorLogicColumn:
            resolveColumnName(fill.color.columnId, columnMetadata, stats) ??
            fill.color.columnName ??
            '',
          colorLogicScheme: fill.color.scheme,
          colorLogicType: fill.color.mode === STYLE_MODE.CATEGORY ? STYLE_MODE.CATEGORY : 'value',
          colorLogicEnabled: true,
          dataMin: fill.color.mode === STYLE_MODE.RANGE ? fill.color.dataMin : undefined,
          dataMax: fill.color.mode === STYLE_MODE.RANGE ? fill.color.dataMax : undefined,
          customColors:
            fill.color.mode === STYLE_MODE.CATEGORY ? fill.color.customColors : undefined,
        };
      }

      if (fill.opacity && fill.opacity.mode === STYLE_MODE.RANGE) {
        config.opacityLogic = {
          propertyName: 'fill-opacity',
          column:
            resolveColumnName(fill.opacity.columnId, columnMetadata, stats) ??
            fill.opacity.columnName ??
            '',
          min: fill.opacity.min,
          max: fill.opacity.max,
          direction: fill.opacity.direction,
        };
      }
    }

    if (border && hasPolygons) {
      config.line = {
        'line-color': border.color?.mode === STYLE_MODE.FIXED ? border.color.value : '#000000',
        'line-width': border.width?.mode === STYLE_MODE.FIXED ? border.width.value : 1,
        'line-opacity': border.opacity?.mode === STYLE_MODE.FIXED ? border.opacity.value : 1,
        'line-dasharray': getBorderDashArray(border.borderType || 'solid'),
        'line-gap-width': getBorderLineGapWidth(border.borderType || 'solid'),
      };

      if (border.width && border.width.mode === STYLE_MODE.RANGE) {
        config.widthLogic = {
          propertyName: 'line-width',
          column:
            resolveColumnName(border.width.columnId, columnMetadata, stats) ??
            border.width.columnName ??
            '',
          min: border.width.min,
          max: border.width.max,
          direction: border.width.direction,
        };
      }

      if (border.opacity && border.opacity.mode === STYLE_MODE.RANGE && !config.opacityLogic) {
        config.opacityLogic = {
          propertyName: 'line-opacity',
          column:
            resolveColumnName(border.opacity.columnId, columnMetadata, stats) ??
            border.opacity.columnName ??
            '',
          min: border.opacity.min,
          max: border.opacity.max,
          direction: border.opacity.direction,
        };
      }
    }

    if (label && (label.columnId || label.labelText) && hasPolygons) {
      config.labelLogic = {
        labelBy: resolveColumnName(label.columnId, columnMetadata, stats) ?? label.columnName ?? '',
        labelText: resolveLabelTextColumns(label.labelText, columnMetadata, stats),
        size: label.size
          ? toLabelSizeMode(label.size)
          : toLabelSizeMode(DEFAULT_STYLE_VALUES.labelLogic.size),
        color: label.color?.mode === STYLE_MODE.FIXED ? label.color.value : '#000000',
        haloColor: label.haloColor?.mode === STYLE_MODE.FIXED ? label.haloColor.value : '#000000',
        haloWidth: label.haloWidth
          ? toLabelSizeMode(label.haloWidth)
          : toLabelSizeMode(DEFAULT_STYLE_VALUES.labelLogic.haloWidth),
        fontStyle: label.fontStyle,
      };
    }
  }

  if (v2Style.line && hasLines && !hasPolygons) {
    const color = v2Style.line.color;
    const opacity = v2Style.line.opacity;
    const width = v2Style.line.width;
    const borderType = v2Style.line.borderType;

    config.line = {
      'line-color': color?.mode === STYLE_MODE.FIXED ? color.value : '#2DD4BF',
      'line-width': width?.mode === STYLE_MODE.FIXED ? width.value : 3,
      'line-opacity': opacity?.mode === STYLE_MODE.FIXED ? opacity.value : 0.8,
      'line-dasharray': getBorderDashArray(borderType || 'solid'),
      'line-gap-width': getBorderLineGapWidth(borderType || 'solid'),
    };

    if (color && color.mode !== STYLE_MODE.FIXED && !config.colorLogic) {
      config.colorLogic = {
        colorLogicColumn:
          resolveColumnName(color.columnId, columnMetadata, stats) ?? color.columnName ?? '',
        colorLogicScheme: color.scheme,
        colorLogicType: color.mode === STYLE_MODE.CATEGORY ? STYLE_MODE.CATEGORY : 'value',
        colorLogicEnabled: true,
        dataMin: color.mode === STYLE_MODE.RANGE ? color.dataMin : undefined,
        dataMax: color.mode === STYLE_MODE.RANGE ? color.dataMax : undefined,
        customColors: color.mode === STYLE_MODE.CATEGORY ? color.customColors : undefined,
      };
    }

    if (opacity && opacity.mode === STYLE_MODE.RANGE && !config.opacityLogic) {
      config.opacityLogic = {
        propertyName: 'line-opacity',
        column:
          resolveColumnName(opacity.columnId, columnMetadata, stats) ?? opacity.columnName ?? '',
        min: opacity.min,
        max: opacity.max,
        direction: opacity.direction,
      };
    }

    if (width && width.mode === STYLE_MODE.RANGE && !config.widthLogic) {
      config.widthLogic = {
        propertyName: 'line-width',
        column: resolveColumnName(width.columnId, columnMetadata, stats) ?? width.columnName ?? '',
        min: width.min,
        max: width.max,
        direction: width.direction,
      };
    }
  }

  if (!config.colorLogic) {
    config.colorLogic = {
      colorLogicColumn: DEFAULT_STYLE_VALUES.colorLogicColumn,
      colorLogicScheme: DEFAULT_STYLE_VALUES.colorLogicScheme,
      colorLogicType: DEFAULT_STYLE_VALUES.colorLogicType,
      colorLogicEnabled: DEFAULT_STYLE_VALUES.colorLogicEnabled,
    };
  }

  if (!config.sizeLogic) {
    config.sizeLogic = {
      sizeBy: DEFAULT_STYLE_VALUES.sizeLogic.sizeBy,
      minSize: DEFAULT_STYLE_VALUES.sizeLogic.minSize,
      maxSize: DEFAULT_STYLE_VALUES.sizeLogic.maxSize,
      direction: DEFAULT_STYLE_VALUES.sizeLogic.direction,
    };
  }

  if (!config.labelLogic) {
    config.labelLogic = {
      labelBy: DEFAULT_STYLE_VALUES.labelLogic.labelBy,
      size: toLabelSizeMode(DEFAULT_STYLE_VALUES.labelLogic.size),
      color: DEFAULT_STYLE_VALUES.labelLogic.color,
      haloColor: DEFAULT_STYLE_VALUES.labelLogic.haloColor,
      haloWidth: toLabelSizeMode(DEFAULT_STYLE_VALUES.labelLogic.haloWidth),
      fontStyle: DEFAULT_STYLE_VALUES.labelLogic.fontStyle,
    };
  }

  if (v2Style.raster) {
    config.raster = { opacity: v2Style.raster.opacity };
  }

  return config;
}
